import { User, RaceGoal, Workout, WorkoutFormData, DashboardStats, WeeklyPlan, GoalFeasibility, TrainingZones, RaceCard, RaceDetail, SearchFilters, SearchResult, ScrapeJob, RaceConfidence, WeekOverride, ConstraintReason } from '../types';

// In production (REACT_APP_API_URL=''), requests go to the same origin (Flask serves React).
// In development (REACT_APP_API_URL unset), fall back to localhost:5001.
export const getApiUrl = (): string => {
  if (process.env.REACT_APP_API_URL !== undefined) {
    return process.env.REACT_APP_API_URL; // '' for same-origin production
  }
  const { protocol, hostname } = window.location;
  return `${protocol}//${hostname}:5001`;
};

class ApiService {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const apiUrl = getApiUrl(); // Compute dynamically each time
    const response = await fetch(`${apiUrl}${endpoint}`, {
      ...options,
      credentials: 'include', // Important for session cookies
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (response.status === 403) {
      const data = await response.json().catch(() => ({}));
      if (data.error === 'account_blocked') {
        localStorage.setItem('blocked_message', data.message || '');
        window.location.href = '/blocked';
        return undefined as unknown as T;
      }
      throw new Error(data.error || `HTTP 403`);
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // Auth endpoints
  async checkAuth(): Promise<{ authenticated: boolean; user?: User; unit_preference?: string; needs_onboarding?: boolean }> {
    return this.request('/auth/check');
  }

  async getCurrentUser(): Promise<User> {
    return this.request('/auth/user');
  }

  async logout(): Promise<void> {
    await this.request('/auth/logout', { method: 'POST' });
  }

  // Profile endpoints
  async getRaceGoal(): Promise<RaceGoal> {
    return this.request('/api/profile/race-goal');
  }

  async setRaceGoal(data: RaceGoal): Promise<void> {
    await this.request('/api/profile/race-goal', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateRaceGoal(data: Partial<RaceGoal>): Promise<void> {
    await this.request('/api/profile/race-goal', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Multi-race continuation: roll the current race into a new goal race without
  // a base rebuild (recovery week auto-created after the tune-up).
  async continueIntoNextRace(data: {
    race_name: string;
    race_date: string;
    race_distance_miles: number;
    race_elevation_gain_ft?: number;
    goal_finish_time_seconds?: number;
  }): Promise<{
    message: string;
    current_race_name: string;
    current_race_date: string;
    next_race_name: string;
    next_race_date: string;
  }> {
    return this.request('/api/training/continue-race', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Week-level constraints (travel / reduced / recovery / unavailable weeks)
  async getWeekOverrides(): Promise<{ overrides: WeekOverride[] }> {
    return this.request('/api/training/week-overrides');
  }

  async setWeekOverride(data: {
    week_start_date: string;
    reason: ConstraintReason;
    max_mileage?: number | null;
    max_days?: number | null;
    note?: string | null;
  }): Promise<{ message: string; override: WeekOverride }> {
    return this.request('/api/training/week-overrides', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteWeekOverride(weekStartDate: string): Promise<void> {
    await this.request(`/api/training/week-overrides/${weekStartDate}`, {
      method: 'DELETE',
    });
  }

  // Workout endpoints
  async getWorkouts(limit = 10, offset = 0): Promise<Workout[]> {
    return this.request(`/api/workouts?limit=${limit}&offset=${offset}`);
  }

  async getWorkout(id: number): Promise<Workout> {
    return this.request(`/api/workouts/${id}`);
  }

  async logWorkout(data: WorkoutFormData): Promise<{
    id: number;
    message: string;
    avg_pace_sec_per_mile: number;
    workout_date: string;
    linked_to_plan: boolean;
    adherence_message?: string;
    rest_day_warning?: string;
  }> {
    return this.request('/api/workouts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteWorkout(id: number): Promise<void> {
    await this.request(`/api/workouts/${id}`, { method: 'DELETE' });
  }

  async updatePerceivedEffort(id: number, effort: string): Promise<{ id: number; perceived_effort: string }> {
    return this.request(`/api/workouts/${id}/effort`, {
      method: 'PATCH',
      body: JSON.stringify({ perceived_effort: effort }),
    });
  }

  async analyzeWorkout(id: number): Promise<{
    message: string;
    analysis: string;
    analyzed_at: string;
  }> {
    return this.request(`/api/workouts/${id}/analyze`, { method: 'POST' });
  }

  async getWorkoutAnalysis(id: number): Promise<{
    analysis: string;
    analyzed_at: string;
  }> {
    return this.request(`/api/workouts/${id}/analysis`);
  }

  // Dashboard endpoints
  async getDashboardStats(): Promise<DashboardStats> {
    return this.request('/api/dashboard/stats');
  }

  async getDashboardCharts(days: 7 | 30 | 90 = 7): Promise<any> {
    return this.request(`/api/dashboard/charts?days=${days}`);
  }

  async getWeeklySummary(weeks = 4): Promise<any> {
    return this.request(`/api/dashboard/weekly-summary?weeks=${weeks}`);
  }

  async getRaceConfidence(): Promise<RaceConfidence> {
    return this.request('/api/dashboard/race-confidence');
  }

  async getDashboardIntelligence(): Promise<any> {
    return this.request('/api/dashboard/intelligence');
  }

  async getHRZones(days: 7 | 30 | 90 = 90): Promise<any> {
    return this.request(`/api/dashboard/hr-zones?days=${days}`);
  }

  async getRaceHistory(): Promise<any> {
    return this.request('/api/race-history');
  }

  async markWorkoutAsRace(workoutId: number, raceName?: string, trainingPlanId?: number): Promise<any> {
    return this.request(`/api/race-history/workouts/${workoutId}/mark-race`, {
      method: 'POST',
      body: JSON.stringify({ race_name: raceName, training_plan_id: trainingPlanId }),
    });
  }

  async unmarkWorkoutAsRace(workoutId: number): Promise<any> {
    return this.request(`/api/race-history/workouts/${workoutId}/unmark-race`, { method: 'POST' });
  }

  // Training endpoints
  async generatePlan(
    regenerateCurrent: boolean = false,
    overrides?: { run_days?: string[]; strength_days?: string[]; cycling_days?: string[]; walking_days?: string[]; rest_days?: string[] },
    targetWeekStart?: string,
    runnerNote?: string
  ): Promise<{
    message: string;
    plan: WeeklyPlan;
    max_mileage_set?: boolean;
    recommended_max_mileage?: number;
    info?: string;
  }> {
    return this.request('/api/training/generate-plan', {
      method: 'POST',
      body: JSON.stringify({
        regenerate_current: regenerateCurrent,
        ...(targetWeekStart          && { target_week_start:      targetWeekStart }),
        ...(overrides?.run_days      && { override_run_days:      overrides.run_days }),
        ...(overrides?.strength_days && { override_strength_days: overrides.strength_days }),
        ...(overrides?.cycling_days  && { override_cycling_days:  overrides.cycling_days }),
        ...(overrides?.walking_days  && { override_walking_days:  overrides.walking_days }),
        ...(overrides?.rest_days     && { override_rest_days:     overrides.rest_days }),
        ...(runnerNote               && { runner_note:            runnerNote }),
      })
    });
  }

  async getCurrentPlan(weekStart?: string): Promise<WeeklyPlan> {
    const params = new URLSearchParams();
    if (weekStart) params.set('week_start', weekStart);
    // Dev only: forward ?viewUserId= from the browser URL to the backend
    const pageParams = new URLSearchParams(window.location.search);
    const viewUserId = pageParams.get('viewUserId');
    if (viewUserId) params.set('viewUserId', viewUserId);
    const qs = params.toString();
    return this.request(`/api/training/current-plan${qs ? `?${qs}` : ''}`);
  }

  async getPlanHistory(): Promise<{ plans: Array<any> }> {
    const viewUserId = new URLSearchParams(window.location.search).get('viewUserId');
    return this.request(viewUserId ? `/api/training/plans/history?viewUserId=${viewUserId}` : '/api/training/plans/history');
  }

  async getGoalFeasibility(): Promise<GoalFeasibility> {
    const viewUserId = new URLSearchParams(window.location.search).get('viewUserId');
    return this.request(viewUserId ? `/api/training/feasibility?viewUserId=${viewUserId}` : '/api/training/feasibility');
  }

  async calculateZones(): Promise<{ message: string; hr_zones: any; pace_zones: any }> {
    return this.request('/api/training/calculate-zones', { method: 'POST' });
  }

  async getTrainingZones(): Promise<TrainingZones> {
    return this.request('/api/training/zones');
  }

  // Strava sync
  async syncStravaActivities(daysBack = 90): Promise<{
    message: string;
    success: boolean;
    imported: number;
    updated: number;
    skipped: number;
    total_processed: number;
  }> {
    return this.request('/auth/strava/sync', {
      method: 'POST',
      body: JSON.stringify({ days_back: daysBack })
    });
  }

  // Workout tracking and adherence
  async getPlannedWorkouts(weekStart?: string): Promise<{
    week_start: string;
    week_end: string;
    workouts: any[];
    extra_workouts: Record<string, any[]>;
    cross_training: any[];
  }> {
    const params = new URLSearchParams();
    if (weekStart) params.set('week_start', weekStart);
    // Dev only: forward ?viewUserId= from the browser URL to the backend
    const pageParams = new URLSearchParams(window.location.search);
    const viewUserId = pageParams.get('viewUserId');
    if (viewUserId) params.set('viewUserId', viewUserId);
    const qs = params.toString();
    return this.request(`/api/training/planned-workouts${qs ? `?${qs}` : ''}`);
  }

  async linkWorkoutToPlan(plannedWorkoutId: number, completedWorkoutId: number): Promise<{
    message: string;
    adherence_score: number;
    status: string;
  }> {
    return this.request('/api/training/link-workout', {
      method: 'POST',
      body: JSON.stringify({
        planned_workout_id: plannedWorkoutId,
        completed_workout_id: completedWorkoutId
      })
    });
  }

  async getWeeklyAdherence(weekStart?: string): Promise<{
    week_start: string;
    week_end: string;
    workouts_planned: number;
    workouts_completed: number;
    workouts_partial: number;
    workouts_missed: number;
    mileage_planned: number;
    mileage_completed: number;
    mileage_completion_percentage: number;
    long_run_completed: boolean;
    quality_workout_completed: boolean;
    adherence_score: number;
  }> {
    const params = weekStart ? `?week_start=${weekStart}` : '';
    return this.request(`/api/training/weekly-adherence${params}`);
  }

  async getAdherenceHistory(weeks = 4): Promise<{
    history: any[];
    cumulative: {
      avg_weekly_adherence: number;
      weeks_tracked: number;
      consistency_streak: number;
      long_run_completion_rate: number;
      quality_workout_completion_rate: number;
    };
  }> {
    return this.request(`/api/training/adherence-history?weeks=${weeks}`);
  }

  async movePlannedWorkout(workoutId: number, newDate: string): Promise<{
    message: string;
    workout: {
      id: number;
      old_date: string;
      new_date: string;
      new_day: string;
      workout_type: string;
    };
  }> {
    return this.request(`/api/training/planned-workouts/${workoutId}/move`, {
      method: 'PATCH',
      body: JSON.stringify({ new_date: newDate })
    });
  }

  async deletePlannedWorkout(workoutId: number): Promise<{
    message: string;
    deleted_workout: {
      date: string;
      day: string;
      type: string;
    };
  }> {
    return this.request(`/api/training/planned-workouts/${workoutId}`, {
      method: 'DELETE'
    });
  }

  async pausePlan(): Promise<{
    message: string;
    plan_status: string;
    paused_at: string;
  }> {
    return this.request('/api/profile/plan/pause', {
      method: 'POST'
    });
  }

  async resumePlan(): Promise<{
    message: string;
    plan_status: string;
  }> {
    return this.request('/api/profile/plan/resume', {
      method: 'POST'
    });
  }

  async completePlan(): Promise<{
    message: string;
    plan_status: string;
    completed_at: string;
  }> {
    return this.request('/api/profile/plan/complete', {
      method: 'POST'
    });
  }

  async cancelPlan(): Promise<{
    message: string;
    plan_status: string;
    cancelled_at: string;
  }> {
    return this.request('/api/profile/plan/cancel', {
      method: 'POST'
    });
  }

  async updateTimezone(): Promise<void> {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    await this.request('/api/profile/timezone', {
      method: 'PATCH',
      body: JSON.stringify({ timezone: tz }),
    });
  }

  async getPlanStatus(): Promise<{
    plan_status: string;
    paused_at?: string;
    completed_at?: string;
  }> {
    return this.request('/api/profile/plan/status');
  }

  // ── Coach / Running Brain ──────────────────────────────────────────────────

  async sendCoachMessage(message: string): Promise<{
    message: string;
    actions: string[];
    timestamp: string;
  }> {
    return this.request('/api/coach/chat', {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
  }

  async getCoachHistory(): Promise<{
    messages: Array<{
      id: number;
      role: 'user' | 'assistant';
      content: string;
      actions_taken: string[] | null;
      created_at: string;
    }>;
  }> {
    return this.request('/api/coach/history');
  }

  async clearCoachHistory(): Promise<{ ok: boolean }> {
    return this.request('/api/coach/history', { method: 'DELETE' });
  }

  async getCoachInsights(): Promise<{
    insights: Array<{
      id: number;
      insight_type: string;
      body_part?: string;
      description: string;
      severity?: string;
      created_at: string;
    }>;
  }> {
    return this.request('/api/coach/insights');
  }

  // ── Admin ──────────────────────────────────────────────────────────────────

  async adminLogin(email: string, password: string): Promise<{ ok: boolean; email: string }> {
    return this.request('/admin/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async adminLogout(): Promise<void> {
    await this.request('/admin/logout', { method: 'POST' });
  }

  async adminCheck(): Promise<{ authenticated: boolean; email?: string }> {
    return this.request('/admin/check');
  }

  async adminGetUsers(): Promise<any[]> {
    return this.request('/admin/users');
  }

  async adminBlockUser(userId: number, reason: string): Promise<void> {
    await this.request(`/admin/users/${userId}/block`, {
      method: 'PUT',
      body: JSON.stringify({ reason }),
    });
  }

  async adminUnblockUser(userId: number): Promise<void> {
    await this.request(`/admin/users/${userId}/unblock`, { method: 'PUT' });
  }

  async adminSetInactive(userId: number, inactive: boolean): Promise<void> {
    await this.request(`/admin/users/${userId}/${inactive ? 'deactivate' : 'activate'}`, { method: 'PUT' });
  }

  async saveWaitlistEmail(userId: number, email: string): Promise<void> {
    await this.request('/auth/waitlist-email', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, email }),
    });
  }

  async adminGetPendingUsers(): Promise<any[]> {
    return this.request('/admin/users/pending');
  }

  async adminApproveUser(userId: number): Promise<void> {
    await this.request(`/admin/users/${userId}/approve`, { method: 'PUT' });
  }

  async adminDeleteUser(userId: number): Promise<void> {
    await this.request(`/admin/users/${userId}`, { method: 'DELETE' });
  }

  async adminDeleteUserPlans(userId: number): Promise<{ plans_deleted: number }> {
    return this.request(`/admin/users/${userId}/delete-plans`, { method: 'DELETE' });
  }

  async adminGetStats(): Promise<{
    total_users: number;
    new_users_this_week: number;
    blocked_users: number;
    coach_messages_this_week: number;
  }> {
    return this.request('/admin/stats');
  }

  async adminGetConfig(): Promise<Record<string, { value: string; description: string }>> {
    return this.request('/admin/config');
  }

  async adminUpdateConfig(key: string, value: string): Promise<void> {
    await this.request(`/admin/config/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    });
  }

  // ── RaceFinder ────────────────────────────────────────────────────────────

  async searchRaces(filters: Partial<SearchFilters> & { page?: number; per_page?: number }): Promise<SearchResult> {
    const params = new URLSearchParams();
    if (filters.nl_query) params.set('nl_query', filters.nl_query);
    if (filters.q) params.set('q', filters.q);
    if (filters.state) params.set('state', filters.state);
    if (filters.city) params.set('city', filters.city);
    if (filters.zip) params.set('zip', filters.zip);
    if (filters.radius_miles) params.set('radius', String(filters.radius_miles));
    if (filters.date_from) params.set('date_from', filters.date_from);
    if (filters.date_to) params.set('date_to', filters.date_to);
    if (filters.distances?.length) params.set('distances', filters.distances.join(','));
    if (filters.terrains?.length) params.set('terrains', filters.terrains.join(','));
    if (filters.status) params.set('status', filters.status);
    if (filters.sort) params.set('sort', filters.sort);
    if (filters.lat != null) params.set('lat', String(filters.lat));
    if (filters.lng != null) params.set('lng', String(filters.lng));
    if (filters.page) params.set('page', String(filters.page));
    if (filters.per_page) params.set('per_page', String(filters.per_page));
    return this.request(`/api/races/search?${params.toString()}`);
  }

  async getRace(slug: string): Promise<RaceDetail> {
    return this.request(`/api/races/${slug}`);
  }

  async getSimilarRaces(slug: string): Promise<RaceCard[]> {
    return this.request(`/api/races/${slug}/similar`);
  }

  async getCuratedUpcoming(): Promise<RaceCard[]> {
    return this.request('/api/races/curated/upcoming');
  }

  async getRaceReddit(slug: string): Promise<{ posts: any[]; fetched_at: string }> {
    return this.request(`/api/races/${slug}/enrich/reddit`);
  }

  async getRaceAiSummary(slug: string): Promise<{ summary: string; generated_at: string }> {
    return this.request(`/api/races/${slug}/enrich/ai-summary`);
  }

  async getRaceWeather(slug: string): Promise<{
    avg_temp_max_f?: number;
    avg_temp_min_f?: number;
    avg_precip_inch?: number;
    avg_wind_mph?: number;
    data_years: number;
    fetched_at: string;
  }> {
    return this.request(`/api/races/${slug}/enrich/weather`);
  }

  async submitReview(raceId: number, data: {
    overall_rating: number;
    course_scenery?: number;
    organization?: number;
    aid_stations?: number;
    crowd_support?: number;
    medal_swag?: number;
    post_race?: number;
    parking_logistics?: number;
    review_text?: string;
    distance_ran?: string;
    race_date_ran?: string;
  }): Promise<{ ok: boolean; review_id: number }> {
    return this.request(`/api/races/${raceId}/reviews`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ── Admin scraper monitoring ──────────────────────────────────────────────

  async adminGetScrapeJobs(): Promise<ScrapeJob[]> {
    return this.request('/admin/scrape/jobs');
  }

  async adminTriggerScrape(source: string): Promise<{ ok: boolean; message: string }> {
    return this.request(`/admin/scrape/trigger/${source}`, { method: 'POST' });
  }

  async adminToggleScraper(source: string): Promise<{ ok: boolean; enabled: boolean }> {
    return this.request(`/admin/scrape/toggle/${source}`, { method: 'POST' });
  }

  // ── Admin email ─────────────────────────────────────────────────────────

  async adminSendWeeklySummary(dryRun = false): Promise<any> {
    return this.request('/admin/email/weekly-summary', {
      method: 'POST',
      body: JSON.stringify({ dry_run: dryRun }),
    });
  }

  // ── Admin curated races ───────────────────────────────────────────────────

  async adminGetCurated(): Promise<any[]> {
    return this.request('/admin/curated');
  }

  async adminAddCurated(data: any): Promise<{ ok: boolean; id: number; slug: string }> {
    return this.request('/admin/curated', { method: 'POST', body: JSON.stringify(data) });
  }

  async adminUpdateCurated(id: number, data: any): Promise<{ ok: boolean }> {
    return this.request(`/admin/curated/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async adminDeleteCurated(id: number): Promise<{ ok: boolean }> {
    return this.request(`/admin/curated/${id}`, { method: 'DELETE' });
  }

  // ── Onboarding + Race Recommender ──────────────────────────────────────────

  async getFitnessAssessment(): Promise<any> {
    return this.request('/api/training/fitness-assessment');
  }

  async getRaceRecommendations(lat?: number, lng?: number, afterDate?: string, zip?: string): Promise<any> {
    const params = new URLSearchParams();
    if (lat != null) params.set('lat', String(lat));
    if (lng != null) params.set('lng', String(lng));
    if (afterDate) params.set('after_date', afterDate);
    if (zip) params.set('zip', zip);
    const qs = params.toString();
    return this.request(`/api/races/recommend${qs ? '?' + qs : ''}`);
  }

  async completeOnboarding(path: string, location?: { latitude: number; longitude: number; label: string }): Promise<any> {
    return this.request('/api/training/onboarding/complete', {
      method: 'POST',
      body: JSON.stringify({ path, location }),
    });
  }

  async adminGetScrapeStats(): Promise<{
    total_races: number;
    sources: Record<string, {
      enabled: boolean;
      is_running: boolean;
      last_status: string | null;
      last_run: string | null;
      last_started: string | null;
      last_error: string | null;
      last_races_found: number;
      last_races_new: number;
      last_races_updated: number;
      duration_seconds: number | null;
    }>;
  }> {
    return this.request('/admin/scrape/stats');
  }

  // Engagement events — fire-and-forget, never throws into the UI.
  // event_type must be one of the allowed values in routes/event_routes.py.
  async trackEvent(event_type: string, metadata?: Record<string, unknown>): Promise<void> {
    try {
      await this.request('/api/events', {
        method: 'POST',
        body: JSON.stringify({ event_type, metadata }),
      });
    } catch {
      // Swallow — engagement tracking should never break the UI.
    }
  }

  // Latest weekly AI insight for the dashboard 'Coach's Take' card.
  // Returns null if no insight has been generated yet (HTTP 204).
  async getWeeklyInsight(): Promise<{
    id: number;
    week_start_date: string;
    headline: string;
    follow_up_prompt: string | null;
    created_at: string;
  } | null> {
    const apiUrl = getApiUrl();
    const response = await fetch(`${apiUrl}/api/insights/weekly`, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    if (response.status === 204) return null;
    if (!response.ok) throw new Error(`Failed to load weekly insight: ${response.status}`);
    return response.json();
  }
}

const api = new ApiService();
export default api;
